(() => {
  const doofinderConfigCache = new Map();
  const storageKey = "sc-customizer-min-price-cache-v2";
  let persistedConfigCache = null;

  function normalizeCacheKey(url) {
    try {
      const parsedUrl = new URL(url, window.location.origin);
      return parsedUrl.pathname.replace(/\/+$/, "");
    } catch (error) {
      return String(url || "");
    }
  }

  function readPersistedConfigCache() {
    if (persistedConfigCache !== null) {
      return persistedConfigCache;
    }

    try {
      persistedConfigCache = JSON.parse(window.localStorage.getItem(storageKey) || "{}");
    } catch (error) {
      persistedConfigCache = {};
    }

    return persistedConfigCache;
  }

  function writePersistedConfigCache() {
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(readPersistedConfigCache()));
    } catch (error) {
      // Ignore storage write failures.
    }
  }

  function getCachedConfig(url) {
    const cacheKey = normalizeCacheKey(url);
    const persistedEntry = readPersistedConfigCache()[cacheKey];

    if (persistedEntry !== undefined) {
      if (!persistedEntry) {
        return null;
      }

      return persistedEntry;
    }

    return null;
  }

  function setCachedConfig(url, value) {
    const cacheKey = normalizeCacheKey(url);

    if (!value) {
      delete readPersistedConfigCache()[cacheKey];
      writePersistedConfigCache();
      return;
    }

    readPersistedConfigCache()[cacheKey] = value;
    writePersistedConfigCache();
  }

  function clearCachedConfig(url) {
    if (url) {
      delete readPersistedConfigCache()[normalizeCacheKey(url)];
    } else {
      persistedConfigCache = {};
    }

    writePersistedConfigCache();
  }

  function parseNumber(value) {
    if (value == null) {
      return 0;
    }

    const normalized = String(value).replace(",", ".").trim();
    const parsed = Number(normalized);

    return Number.isFinite(parsed) ? parsed : 0;
  }

  function buildExpression(formula, calc1, calc2, calc3) {
    return String(formula || "")
      .replace(/\bcalc1\b/g, String(calc1))
      .replace(/\bcalc2\b/g, String(calc2))
      .replace(/\bcalc3\b/g, String(calc3));
  }

  function evaluateFormula(formula, calc1, calc2, calc3) {
    const expression = buildExpression(formula, calc1, calc2, calc3);

    if (!/^[0-9+\-*/().\s]+$/.test(expression)) {
      return null;
    }

    try {
      const result = Function(`"use strict"; return (${expression});`)();

      if (!Number.isFinite(result)) {
        return null;
      }

      return Math.floor(result);
    } catch (error) {
      return null;
    }
  }

  function formatFromPrice(priceCents, moneyFormat) {
    const formattedPrice = typeof window.formatMoney === "function"
      ? window.formatMoney(priceCents, moneyFormat)
      : String(priceCents / 100);

    return window.themeVariables?.strings?.collectionFromPriceHtml?.replace("@price@", formattedPrice) || formattedPrice;
  }

  function formatExactPrice(priceCents, moneyFormat) {
    return typeof window.formatMoney === "function"
      ? window.formatMoney(priceCents, moneyFormat)
      : String(priceCents / 100);
  }

  function formatPrice(priceCents, moneyFormat, displayMode) {
    if (displayMode === "exact") {
      return formatExactPrice(priceCents, moneyFormat);
    }

    return formatFromPrice(priceCents, moneyFormat);
  }

  function renderPriceMarkup(priceCents, moneyFormat, displayMode, priceClass = "") {
    const className = priceClass ? ` class="${priceClass}"` : "";

    return `
      <span${className}>
        <span class="visually-hidden">${window.themeVariables?.strings?.productSalePrice || ""}</span>
        ${formatPrice(priceCents, moneyFormat, displayMode)}
      </span>
    `;
  }

  function renderCustomizerNode(node, displayModeOverride) {
    if (!(node instanceof HTMLElement)) {
      return false;
    }

    const calc1 = parseNumber(node.dataset.calc1);
    const calc2 = parseNumber(node.dataset.calc2);
    const calc3 = parseNumber(node.dataset.calc3);
    const minPrice = evaluateFormula(node.dataset.formula, calc1, calc2, calc3);

    if (minPrice == null) {
      return false;
    }

    node.setAttribute("data-sc-customizer-min-price-ready", "true");
    node.innerHTML = renderPriceMarkup(
      minPrice * 100,
      node.dataset.moneyFormat || "",
      displayModeOverride || node.dataset.displayMode || "from"
    );

    return true;
  }

  function updateCustomizerCartPayload(node) {
    if (!(node instanceof HTMLElement)) {
      return false;
    }

    const form = node.closest("form");
    if (!(form instanceof HTMLFormElement)) {
      return false;
    }

    const quantityInput = form.querySelector("[data-sc-customizer-quantity-input]");
    if (!(quantityInput instanceof HTMLInputElement)) {
      return false;
    }

    const calc1 = parseNumber(node.dataset.calc1);
    const calc2 = parseNumber(node.dataset.calc2);
    const calc3 = parseNumber(node.dataset.calc3);
    const minPrice = evaluateFormula(node.dataset.formula, calc1, calc2, calc3);

    if (minPrice == null) {
      return false;
    }

    const timestamp = String(Date.now());
    const dateInput = node.querySelector("[data-sc-customizer-date]");
    const requestIdInput = node.querySelector("[data-sc-customizer-request-id]");

    quantityInput.value = String(minPrice);

    if (dateInput instanceof HTMLInputElement) {
      dateInput.value = timestamp;
    }

    if (requestIdInput instanceof HTMLInputElement) {
      requestIdInput.value = timestamp;
    }

    node.setAttribute("data-sc-customizer-min-cart-ready", "true");

    return true;
  }

  function getProductUrlFromNode(node) {
    let current = node;

    while (current && current !== document.body) {
      const link = current.matches?.("a[href]") ? current : current.querySelector?.("a[href]");

      if (link) {
        try {
          const url = new URL(link.getAttribute("href"), window.location.origin);

          if (url.origin === window.location.origin && url.pathname.includes("/products/")) {
            return url.toString();
          }
        } catch (error) {
          // Ignore malformed URLs and continue walking up the tree.
        }
      }

      current = current.parentElement;
    }

    return null;
  }

  function getCustomizerConfigFromMarkup(root) {
    const sourceNode = root.querySelector("sc-customizer-min-price[data-sc-customizer-owner='main-product'][data-formula]");

    if (!sourceNode) {
      return false;
    }

    return {
      formula: sourceNode.dataset.formula || "",
      calc1: sourceNode.dataset.calc1 || "",
      calc2: sourceNode.dataset.calc2 || "",
      calc3: sourceNode.dataset.calc3 || "",
      moneyFormat: sourceNode.dataset.moneyFormat || ""
    };
  }

  async function getDoofinderCustomizerConfig(url) {
    if (!url) {
      return false;
    }

    const cachedConfig = getCachedConfig(url);

    if (cachedConfig !== null) {
      return cachedConfig;
    }

    const cacheKey = normalizeCacheKey(url);

    if (!doofinderConfigCache.has(cacheKey)) {
      doofinderConfigCache.set(
        cacheKey,
        fetch(url, { credentials: "same-origin" })
          .then((response) => response.text())
          .then((html) => {
            const parser = new DOMParser();
            const documentRoot = parser.parseFromString(html, "text/html");
            const config = getCustomizerConfigFromMarkup(documentRoot);
            if (config) {
              setCachedConfig(url, config);
            } else {
              clearCachedConfig(url);
            }
            return config;
          })
          .catch(() => false)
      );
    }

    return doofinderConfigCache.get(cacheKey);
  }

  async function updateDoofinderPrices(force = false) {
    const pricingNodes = Array.from(document.querySelectorAll(".dfd-card-pricing"));

    await Promise.all(
      pricingNodes.map(async (pricingNode) => {
        if (!(pricingNode instanceof HTMLElement)) {
          return;
        }

        const productUrl = getProductUrlFromNode(pricingNode);
        if (!productUrl) {
          return;
        }

        if (!force && pricingNode.dataset.scCustomizerMinPriceUrl === productUrl) {
          return;
        }

        if (force) {
          pricingNode.removeAttribute("data-sc-customizer-min-price-url");
          pricingNode.removeAttribute("data-sc-customizer-min-price-state");
        }

        let config = getCachedConfig(productUrl);

        if (!config) {
          if (config === null) {
            config = await getDoofinderCustomizerConfig(productUrl);
          }
        }

        if (!config) {
          pricingNode.dataset.scCustomizerMinPriceUrl = productUrl;
          pricingNode.setAttribute("data-sc-customizer-min-price-state", "passthrough");
          return;
        }

        const minPrice = evaluateFormula(
          config.formula,
          parseNumber(config.calc1),
          parseNumber(config.calc2),
          parseNumber(config.calc3)
        );

        if (minPrice == null) {
          pricingNode.dataset.scCustomizerMinPriceUrl = productUrl;
          pricingNode.setAttribute("data-sc-customizer-min-price-state", "passthrough");
          return;
        }

        pricingNode.dataset.scCustomizerMinPriceUrl = productUrl;
        pricingNode.setAttribute("data-sc-customizer-min-price-state", "resolved");
        pricingNode.innerHTML = renderPriceMarkup(minPrice * 100, config.moneyFormat, "from", "dfd-card-price");
      })
    );
  }

  class ScCustomizerMinPrice extends HTMLElement {
    connectedCallback() {
      if (this.getAttribute("data-sc-customizer-min-price-ready") === "true") {
        return;
      }
      renderCustomizerNode(this);
    }
  }

  class ScCustomizerMinCartProperties extends HTMLElement {
    connectedCallback() {
      if (this._boundSubmitHandler) {
        return;
      }

      this._boundSubmitHandler = () => {
        updateCustomizerCartPayload(this);
      };

      this._form = this.closest("form");

      updateCustomizerCartPayload(this);

      if (this._form instanceof HTMLFormElement) {
        this._form.addEventListener("submit", this._boundSubmitHandler, true);
      }
    }

    disconnectedCallback() {
      if (this._form instanceof HTMLFormElement && this._boundSubmitHandler) {
        this._form.removeEventListener("submit", this._boundSubmitHandler, true);
      }

      this._form = null;
      this._boundSubmitHandler = null;
    }
  }

  if (!window.customElements.get("sc-customizer-min-price")) {
    window.customElements.define("sc-customizer-min-price", ScCustomizerMinPrice);
  }

  if (!window.customElements.get("sc-customizer-min-cart-properties")) {
    window.customElements.define("sc-customizer-min-cart-properties", ScCustomizerMinCartProperties);
  }

  window.scCustomizerMinPrice = {
    clearCachedConfig,
    evaluateFormula,
    formatExactPrice,
    formatFromPrice,
    getCustomizerConfigForUrl: getDoofinderCustomizerConfig,
    parseNumber,
    renderCustomizerNode,
    updateCustomizerCartPayload,
    updateDoofinderPrices
  };

  document.addEventListener("doofinder.layer.load", () => {
    updateDoofinderPrices();
  });

  document.addEventListener("doofinder.layer.update", () => {
    updateDoofinderPrices();
  });

  document.addEventListener("doofinder.carousel.load", () => {
    updateDoofinderPrices();
  });
})();
